import React from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { ArrowLeft, Compass, MailPlus } from "lucide-react";

/**
 * MOCKED stub landing page for the "Find an Advisor" and
 * "How TravelBio Advisors work" CTAs on the traveler home page.
 * Both routes render this same page with a slightly different
 * headline so the traveler journey doesn't hit a 404 while the
 * real advisor discovery + explainer pages are being designed.
 *
 * Phase-in plan:
 *   • /advisors               → advisor directory + match tool
 *   • /advisors/how-it-works  → editorial "how it works" story
 *
 * Neither exists yet — this component is a waitlist capture stub.
 */

export default function AdvisorsLandingPage() {
  const nav = useNavigate();
  const { pathname } = useLocation();
  const isHowItWorks = pathname.endsWith("/how-it-works");
  const headline = isHowItWorks
    ? "How TravelBio Advisors work"
    : "Find your TravelBio Advisor";
  const sub = isHowItWorks
    ? "We're finishing the editorial that walks through how our advisors are vetted, matched, and rewarded — and how your trip stays yours the whole time."
    : "We're onboarding our first cohort of vetted TravelBio Advisors. Join the waitlist and we'll email you the moment matching goes live.";

  return (
    <div
      data-testid="advisors-stub-page"
      className="min-h-screen w-full bg-white text-tb-navy px-6 py-10 sm:py-16"
    >
      <div className="max-w-3xl mx-auto">
        <button
          type="button"
          onClick={() => nav(-1)}
          data-testid="advisors-stub-back"
          className="inline-flex items-center gap-1.5 font-hand text-tb-navy/70 text-sm hover:text-tb-navy transition tb-tap"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <div className="mt-8 flex items-center gap-3">
          <span className="inline-flex items-center justify-center w-10 h-10 rounded-full border border-tb-gold/50 bg-tb-gold/10">
            <Compass className="w-5 h-5 text-tb-gold-bright" />
          </span>
          <p
            data-testid="advisors-stub-eyebrow"
            className="font-hand text-tb-gold-bright text-xs sm:text-sm tracking-[0.35em] uppercase"
          >
            TravelBio Advisors
          </p>
        </div>

        <h1
          data-testid="advisors-stub-headline"
          className="mt-4 text-tb-navy leading-[1.1]"
          style={{
            fontFamily: '"Playfair Display", serif',
            fontSize: "clamp(2rem, 4.4vw, 3.4rem)",
            textWrap: "balance",
          }}
        >
          {headline}
        </h1>
        <p
          data-testid="advisors-stub-copy"
          className="mt-5 font-hand text-tb-navy/80 text-base sm:text-lg leading-relaxed max-w-2xl"
        >
          {sub}
        </p>

        <div
          data-testid="advisors-stub-waitlist"
          className="mt-10 rounded-2xl border border-tb-navy/15 bg-tb-navy/[0.02] p-5 sm:p-6 max-w-xl"
        >
          <p className="font-hand text-tb-navy text-sm sm:text-base">
            Coming soon — join our waitlist
          </p>
          <p className="mt-1 font-hand text-tb-navy/60 text-xs sm:text-sm">
            We'll only email you when the advisor experience is ready. No spam,
            no drip campaigns.
          </p>
          <button
            type="button"
            data-testid="advisors-stub-waitlist-btn"
            onClick={() => nav("/home")}
            className="mt-4 inline-flex items-center gap-2 rounded-full bg-tb-gold text-tb-navy-deep px-6 py-3 font-hand font-semibold text-sm hover:brightness-105 transition tb-tap"
          >
            <MailPlus className="w-4 h-4" /> Notify me when it's live
          </button>
        </div>
      </div>
    </div>
  );
}
